import type { AnchoredRow } from '../types/anchoredRowTypes'
import type { CompleteMicroSegmentationResult } from '../types/segmentationTypes'
import { preprocessLedgerText } from './ledgerTextPreprocessing'
import {
  buildLedgerSafeMappingReferenceIndex,
  getLedgerSafeActiveMapping,
  resolveLedgerSafeMappingToOrderedIndex
} from './ledgerSafeBoundaryMapping'
import {
  buildCanonicalSplitDisplayWords,
  normalizeSplitComparableSourceWord,
  type LedgerSafeUnitSplitWord as LedgerSafeBoundarySplitWord,
  type SegmentableBoundaryInfo
} from './ledgerSafeBoundaryText'
import {
  getOrderedUnitUuid,
  getOrderedWitnessUnits,
  resolveLedgerSafeSegmentationSourceText,
  type OrderedWitnessUnit
} from './ledgerSafeBoundarySource'
import {
  extractFoliationSpans,
  extractWordSpans,
  getSegmentableWordData
} from './manualStudioSegmentationResult'

export interface LedgerSafeBoundaryContext {
  witnessId: string
  leftRowId: string
  rightRowId: string
  leftUnitUuid: string
  rightUnitUuid: string
  leftUnitLabel: string
  rightUnitLabel: string
  leftStart: number
  leftEnd: number
  rightStart: number
  rightEnd: number
  boundaryWordIndex: number
  minBoundaryWordIndex: number
  maxBoundaryWordIndex: number
  leftUnitText: string
  rightUnitText: string
}

export type LedgerSafeUnitSplitWord = LedgerSafeBoundarySplitWord

export interface LedgerSafeUnitSplitContext {
  witnessId: string
  rowId: string
  unitUuid: string
  unitLabel: string
  sourceText: string
  unitStart: number
  unitEnd: number
  unitText: string
  minSplitBoundaryWordIndex: number
  maxSplitBoundaryWordIndex: number
  words: LedgerSafeUnitSplitWord[]
}

export interface LedgerSafePreparedWitnessContext {
  witnessId: string
  sourceText: string
  orderedUnits: OrderedWitnessUnit[]
  rowIndexById: Map<string, number>
  activeMappingByRowId: Map<string, { unitUuid: string; unitLabel?: string }>
  segmentableWordSpans: Array<{ start: number; end: number }>
  getBoundaryContext: (rowId: string) => LedgerSafeBoundaryContext | null
  getSplitContext: (rowId: string) => LedgerSafeUnitSplitContext | null
  boundaryContextsByLeftRowId: Map<string, LedgerSafeBoundaryContext>
  splitContextsByRowId: Map<string, LedgerSafeUnitSplitContext>
}

export interface LedgerSafeResolvedMappedRow {
  row: AnchoredRow
  rowId: string
  originalUnitUuid: string
  unitUuid: string
  unitLabel?: string
  orderedIndex: number | null
}

export interface LedgerSafeWitnessSourceContext {
  sourceText: string
  orderedUnits: OrderedWitnessUnit[]
  segmentableWordSpans: Array<{ start: number; end: number }>
  wordInfoByOrderedIndex: Map<number, SegmentableBoundaryInfo>
  orderedIndexByReference: Map<string, number>
}

export interface LedgerSafeWitnessContextAnalysis {
  witnessId: string
  sortedRows: AnchoredRow[]
  sourceContext: LedgerSafeWitnessSourceContext
  mappedRows: LedgerSafeResolvedMappedRow[]
  preparedContext: LedgerSafePreparedWitnessContext | null
}

export const sortLedgerSafeRows = (rows: AnchoredRow[]): AnchoredRow[] => [...rows].sort((left, right) => {
  if (left.position !== right.position) {
    return left.position - right.position
  }
  if (left.labelNumber !== right.labelNumber) {
    return left.labelNumber - right.labelNumber
  }
  return left.id.localeCompare(right.id)
})

export const findLedgerSafeSegmentableBoundaryInfo = (
  startPosition: number,
  endPosition: number,
  segmentableWordSpans: Array<{ start: number; end: number }>
): SegmentableBoundaryInfo | null => {
  const coveredIndices: number[] = []
  segmentableWordSpans.forEach((span, index) => {
    if (span.start >= startPosition && span.end <= endPosition) {
      coveredIndices.push(index)
    }
  })
  if (coveredIndices.length === 0) {
    return null
  }
  return {
    startIndex: coveredIndices[0],
    endIndex: coveredIndices[coveredIndices.length - 1]
  }
}

export const buildLedgerSafeSegmentableWordSpans = (
  sourceText: string
): Array<{ start: number; end: number }> => {
  const wordSpans = extractWordSpans(sourceText)
  const foliationSpans = extractFoliationSpans(sourceText, wordSpans)
  return getSegmentableWordData(wordSpans, foliationSpans).segmentableWordSpans
    .filter(span => normalizeSplitComparableSourceWord(sourceText, span).length > 0)
}

// Word spans produced above are ordered and nonoverlapping. Search their bounds
// instead of rescanning the entire witness for each segmentation unit.
const findOrderedWordBoundaryInfo = (
  start: number,
  end: number,
  spans: Array<{ start: number; end: number }>
): SegmentableBoundaryInfo | null => {
  let low = 0
  let high = spans.length
  while (low < high) {
    const middle = (low + high) >>> 1
    if (spans[middle].start < start) low = middle + 1
    else high = middle
  }
  const first = low
  high = spans.length
  while (low < high) {
    const middle = (low + high) >>> 1
    if (spans[middle].end <= end) low = middle + 1
    else high = middle
  }
  return first < low ? { startIndex: first, endIndex: low - 1 } : null
}

export const buildLedgerSafeWitnessSourceContext = ({
  segmentationResult,
  sourceText
}: {
  segmentationResult: CompleteMicroSegmentationResult | null | undefined
  sourceText: string
}): LedgerSafeWitnessSourceContext => {
  const orderedUnits = getOrderedWitnessUnits(segmentationResult)
  const effectiveSourceText = resolveLedgerSafeSegmentationSourceText({
    segmentationResult,
    fallbackSourceText: sourceText
  })
  const segmentableWordSpans = effectiveSourceText
    ? buildLedgerSafeSegmentableWordSpans(effectiveSourceText)
    : []
  const orderedIndexByReference = buildLedgerSafeMappingReferenceIndex({
    orderedUnits,
    idRemap: segmentationResult?.id_remap
  })
  const wordInfoByOrderedIndex = new Map<number, SegmentableBoundaryInfo>()
  orderedUnits.forEach((entry, index) => {
    const unitStart = Number(entry.unit.start_position)
    const unitEnd = Number(entry.unit.end_position)
    if (!Number.isFinite(unitStart) || !Number.isFinite(unitEnd) || unitEnd <= unitStart) {
      return
    }
    const wordInfo = findOrderedWordBoundaryInfo(unitStart, unitEnd, segmentableWordSpans)
    if (wordInfo) {
      wordInfoByOrderedIndex.set(index, wordInfo)
    }
  })

  return {
    sourceText: effectiveSourceText,
    orderedUnits,
    segmentableWordSpans,
    wordInfoByOrderedIndex,
    orderedIndexByReference
  }
}

export const resolveLedgerSafeMappedRows = ({
  rows,
  witnessId,
  orderedUnits,
  orderedIndexByReference
}: {
  rows: AnchoredRow[]
  witnessId: string
  orderedUnits: OrderedWitnessUnit[]
  orderedIndexByReference: Map<string, number>
}): LedgerSafeResolvedMappedRow[] => {
  const claimedOrderedIndexes = new Set<number>()
  const resolvedRows: LedgerSafeResolvedMappedRow[] = []
  rows.forEach(row => {
    const mapping = getLedgerSafeActiveMapping(row, witnessId)
    if (!mapping?.unitUuid) {
      return
    }
    const resolved = resolveLedgerSafeMappingToOrderedIndex({
      mapping,
      orderedUnits,
      orderedIndexByReference,
      claimedOrderedIndexes
    })
    if (typeof resolved?.orderedIndex === 'number') {
      claimedOrderedIndexes.add(resolved.orderedIndex)
    }
    resolvedRows.push({
      row,
      rowId: row.id,
      originalUnitUuid: mapping.unitUuid,
      unitUuid: resolved?.unitUuid || mapping.unitUuid,
      unitLabel: mapping.unitLabel,
      orderedIndex: resolved?.orderedIndex ?? null
    })
  })
  return resolvedRows
}

const getFiniteUnitSpan = (
  unitEntry: OrderedWitnessUnit
): { start: number; end: number } | null => {
  const start = Number(unitEntry.unit.start_position)
  const end = Number(unitEntry.unit.end_position)
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
    return null
  }
  return { start, end }
}

const getDisplayUnitLabel = (
  unitEntry: OrderedWitnessUnit,
  fallbackLabel?: string,
  fallbackUuid?: string
): string =>
  unitEntry.unit.unit_label ||
  unitEntry.unit.unit_id ||
  fallbackLabel ||
  fallbackUuid ||
  getOrderedUnitUuid(unitEntry.unit)

export const buildLedgerSafeBoundaryContextForResolvedRows = ({
  witnessId,
  left,
  right,
  sourceContext
}: {
  witnessId: string
  left: LedgerSafeResolvedMappedRow
  right: LedgerSafeResolvedMappedRow
  sourceContext: LedgerSafeWitnessSourceContext
}): LedgerSafeBoundaryContext | null => {
  const leftOrderedIndex = left.orderedIndex
  const rightOrderedIndex = right.orderedIndex
  if (typeof leftOrderedIndex !== 'number' || typeof rightOrderedIndex !== 'number') {
    return null
  }
  if (rightOrderedIndex !== leftOrderedIndex + 1) {
    return null
  }

  const leftUnitEntry = sourceContext.orderedUnits[leftOrderedIndex]
  const rightUnitEntry = sourceContext.orderedUnits[rightOrderedIndex]
  if (!leftUnitEntry || !rightUnitEntry) {
    return null
  }

  const leftSpan = getFiniteUnitSpan(leftUnitEntry)
  const rightSpan = getFiniteUnitSpan(rightUnitEntry)
  if (!leftSpan || !rightSpan) {
    return null
  }

  const leftWordInfo = sourceContext.wordInfoByOrderedIndex.get(leftOrderedIndex)
  const rightWordInfo = sourceContext.wordInfoByOrderedIndex.get(rightOrderedIndex)
  if (!leftWordInfo || !rightWordInfo) {
    return null
  }
  if (rightWordInfo.startIndex !== leftWordInfo.endIndex + 1) {
    return null
  }

  return {
    witnessId,
    leftRowId: left.rowId,
    rightRowId: right.rowId,
    leftUnitUuid: getOrderedUnitUuid(leftUnitEntry.unit),
    rightUnitUuid: getOrderedUnitUuid(rightUnitEntry.unit),
    leftUnitLabel: getDisplayUnitLabel(leftUnitEntry, left.unitLabel, left.unitUuid),
    rightUnitLabel: getDisplayUnitLabel(rightUnitEntry, right.unitLabel, right.unitUuid),
    leftStart: leftSpan.start,
    leftEnd: leftSpan.end,
    rightStart: rightSpan.start,
    rightEnd: rightSpan.end,
    boundaryWordIndex: rightWordInfo.startIndex,
    minBoundaryWordIndex: leftWordInfo.startIndex + 1,
    maxBoundaryWordIndex: rightWordInfo.endIndex,
    leftUnitText: String(leftUnitEntry.unit.unit_text_content || ''),
    rightUnitText: String(rightUnitEntry.unit.unit_text_content || '')
  }
}

export const buildLedgerSafeUnitSplitContextForResolvedRow = ({
  witnessId,
  mappedRow,
  sourceContext
}: {
  witnessId: string
  mappedRow: LedgerSafeResolvedMappedRow
  sourceContext: LedgerSafeWitnessSourceContext
}): LedgerSafeUnitSplitContext | null => {
  const orderedIndex = mappedRow.orderedIndex
  if (typeof orderedIndex !== 'number') {
    return null
  }
  const unitEntry = sourceContext.orderedUnits[orderedIndex]
  if (!unitEntry) {
    return null
  }

  const unitSpan = getFiniteUnitSpan(unitEntry)
  if (!unitSpan) {
    return null
  }

  const wordInfo = sourceContext.wordInfoByOrderedIndex.get(orderedIndex)
  if (!wordInfo || wordInfo.endIndex <= wordInfo.startIndex) {
    return null
  }

  const splitWords = buildCanonicalSplitDisplayWords({
    unitText: String(unitEntry.unit.unit_text_content || ''),
    sourceText: sourceContext.sourceText,
    wordInfo,
    segmentableWordSpans: sourceContext.segmentableWordSpans
  })
  if (!splitWords) {
    return null
  }
  const splitSourceStart = sourceContext.segmentableWordSpans[splitWords.sourceStartWordIndex]?.start ?? unitSpan.start
  const splitSourceEnd = sourceContext.segmentableWordSpans[splitWords.sourceEndWordIndex]?.end ?? unitSpan.end

  return {
    witnessId,
    rowId: mappedRow.rowId,
    unitUuid: getOrderedUnitUuid(unitEntry.unit),
    unitLabel: getDisplayUnitLabel(unitEntry, mappedRow.unitLabel, mappedRow.unitUuid),
    sourceText: sourceContext.sourceText,
    unitStart: splitSourceStart,
    unitEnd: splitSourceEnd,
    unitText: preprocessLedgerText(String(unitEntry.unit.unit_text_content || '')).workspaceDisplayText,
    minSplitBoundaryWordIndex: splitWords.minSplitBoundaryWordIndex,
    maxSplitBoundaryWordIndex: splitWords.maxSplitBoundaryWordIndex,
    words: splitWords.words
  }
}

export const buildLedgerSafeWitnessContextAnalysis = ({
  rows,
  witnessId,
  segmentationResult,
  sourceText
}: {
  rows: AnchoredRow[]
  witnessId: string
  segmentationResult: CompleteMicroSegmentationResult | null | undefined
  sourceText: string
}): LedgerSafeWitnessContextAnalysis => {
  const sourceContext = buildLedgerSafeWitnessSourceContext({ segmentationResult, sourceText })
  const sortedRows = sortLedgerSafeRows(rows)
  const mappedRows = resolveLedgerSafeMappedRows({
    rows: sortedRows,
    witnessId,
    orderedUnits: sourceContext.orderedUnits,
    orderedIndexByReference: sourceContext.orderedIndexByReference
  })
  const resolvedMappedRows = mappedRows.filter(
    (mappedRow): mappedRow is LedgerSafeResolvedMappedRow & { orderedIndex: number } =>
    typeof mappedRow.orderedIndex === 'number'
  )

  if (
    !witnessId
    || !segmentationResult
    || !sourceContext.sourceText
    || sourceContext.orderedUnits.length === 0
    || sourceContext.segmentableWordSpans.length === 0
  ) {
    return {
      witnessId,
      sortedRows,
      sourceContext,
      mappedRows,
      preparedContext: null
    }
  }

  const rowIndexById = new Map<string, number>()
  sortedRows.forEach((row, index) => {
    rowIndexById.set(row.id, index)
  })

  const activeMappingByRowId = new Map<string, { unitUuid: string; unitLabel?: string }>()
  resolvedMappedRows.forEach(mappedRow => {
    const unitEntry = sourceContext.orderedUnits[mappedRow.orderedIndex]
    activeMappingByRowId.set(mappedRow.rowId, {
      unitUuid: mappedRow.unitUuid,
      unitLabel: unitEntry ? getDisplayUnitLabel(unitEntry, mappedRow.unitLabel, mappedRow.unitUuid) : mappedRow.unitLabel
    })
  })

  const resolvedIndexByRowId = new Map(resolvedMappedRows.map((row, index) => [row.rowId, index]))
  const boundaryContextsByLeftRowId = new Map<string, LedgerSafeBoundaryContext>()
  const splitContextsByRowId = new Map<string, LedgerSafeUnitSplitContext>()
  const checkedBoundaries = new Set<string>()
  const checkedSplits = new Set<string>()
  const getBoundaryContext = (rowId: string): LedgerSafeBoundaryContext | null => {
    if (!checkedBoundaries.has(rowId)) {
      checkedBoundaries.add(rowId)
      const index = resolvedIndexByRowId.get(rowId)
      if (index !== undefined && index + 1 < resolvedMappedRows.length) {
        const context = buildLedgerSafeBoundaryContextForResolvedRows({
          witnessId, left: resolvedMappedRows[index], right: resolvedMappedRows[index + 1], sourceContext,
        })
        if (context) boundaryContextsByLeftRowId.set(rowId, context)
      }
    }
    return boundaryContextsByLeftRowId.get(rowId) || null
  }
  const getSplitContext = (rowId: string): LedgerSafeUnitSplitContext | null => {
    if (!checkedSplits.has(rowId)) {
      checkedSplits.add(rowId)
      const index = resolvedIndexByRowId.get(rowId)
      if (index !== undefined) {
        const context = buildLedgerSafeUnitSplitContextForResolvedRow({ witnessId, mappedRow: resolvedMappedRows[index], sourceContext })
        if (context) splitContextsByRowId.set(rowId, context)
      }
    }
    return splitContextsByRowId.get(rowId) || null
  }

  let allBoundaryContexts: Map<string, LedgerSafeBoundaryContext> | undefined
  let allSplitContexts: Map<string, LedgerSafeUnitSplitContext> | undefined
  const preparedContext: LedgerSafePreparedWitnessContext = {
    witnessId,
    sourceText: sourceContext.sourceText,
    orderedUnits: sourceContext.orderedUnits,
    rowIndexById,
    activeMappingByRowId,
    segmentableWordSpans: sourceContext.segmentableWordSpans,
    getBoundaryContext,
    getSplitContext,
    get boundaryContextsByLeftRowId() {
      if (!allBoundaryContexts) {
        allBoundaryContexts = new Map()
        resolvedMappedRows.forEach(row => {
          const context = getBoundaryContext(row.rowId)
          if (context) allBoundaryContexts!.set(row.rowId, context)
        })
      }
      return allBoundaryContexts
    },
    get splitContextsByRowId() {
      if (!allSplitContexts) {
        allSplitContexts = new Map()
        resolvedMappedRows.forEach(row => {
          const context = getSplitContext(row.rowId)
          if (context) allSplitContexts!.set(row.rowId, context)
        })
      }
      return allSplitContexts
    },
  }

  return {
    witnessId,
    sortedRows,
    sourceContext,
    mappedRows,
    preparedContext
  }
}

export const prepareLedgerSafeWitnessContext = ({
  rows,
  witnessId,
  segmentationResult,
  sourceText
}: {
  rows: AnchoredRow[]
  witnessId: string
  segmentationResult: CompleteMicroSegmentationResult | null | undefined
  sourceText: string
}): LedgerSafePreparedWitnessContext | null => {
  if (!witnessId || !segmentationResult || !sourceText) {
    return null
  }
  return buildLedgerSafeWitnessContextAnalysis({
    rows,
    witnessId,
    segmentationResult,
    sourceText
  }).preparedContext
}

export const createLedgerSafeBoundaryContext = ({
  rows,
  witnessId,
  leftRowId,
  segmentationResult,
  sourceText
}: {
  rows: AnchoredRow[]
  witnessId: string
  leftRowId: string
  segmentationResult: CompleteMicroSegmentationResult | null | undefined
  sourceText: string
}): LedgerSafeBoundaryContext | null => {
  if (!witnessId || !leftRowId || !segmentationResult || !sourceText) {
    return null
  }

  const leftRowIndex = rows.findIndex(row => row.id === leftRowId)
  if (leftRowIndex < 0) {
    return null
  }

  const leftRow = rows[leftRowIndex]
  const leftMapping = leftRow ? getLedgerSafeActiveMapping(leftRow, witnessId) : undefined
  if (!leftMapping?.unitUuid) {
    return null
  }

  let rightRow: AnchoredRow | undefined
  for (let index = leftRowIndex + 1; index < rows.length; index += 1) {
    const candidate = rows[index]
    if (getLedgerSafeActiveMapping(candidate, witnessId)?.unitUuid) {
      rightRow = candidate
      break
    }
  }
  if (!rightRow) {
    return null
  }

  const rightMapping = getLedgerSafeActiveMapping(rightRow, witnessId)
  if (!rightMapping?.unitUuid) {
    return null
  }

  const sourceContext = buildLedgerSafeWitnessSourceContext({ segmentationResult, sourceText })
  const leftResolved = resolveLedgerSafeMappingToOrderedIndex({
    mapping: leftMapping,
    orderedUnits: sourceContext.orderedUnits,
    orderedIndexByReference: sourceContext.orderedIndexByReference
  })
  const claimedForRight = typeof leftResolved?.orderedIndex === 'number'
    ? new Set<number>([leftResolved.orderedIndex])
    : undefined
  const rightResolved = resolveLedgerSafeMappingToOrderedIndex({
    mapping: rightMapping,
    orderedUnits: sourceContext.orderedUnits,
    orderedIndexByReference: sourceContext.orderedIndexByReference,
    claimedOrderedIndexes: claimedForRight
  })

  return buildLedgerSafeBoundaryContextForResolvedRows({
    witnessId,
    left: {
      row: leftRow,
      rowId: leftRow.id,
      originalUnitUuid: leftMapping.unitUuid,
      unitUuid: leftResolved?.unitUuid || leftMapping.unitUuid,
      unitLabel: leftMapping.unitLabel,
      orderedIndex: leftResolved?.orderedIndex ?? null
    },
    right: {
      row: rightRow,
      rowId: rightRow.id,
      originalUnitUuid: rightMapping.unitUuid,
      unitUuid: rightResolved?.unitUuid || rightMapping.unitUuid,
      unitLabel: rightMapping.unitLabel,
      orderedIndex: rightResolved?.orderedIndex ?? null
    },
    sourceContext
  })
}

export const createLedgerSafeUnitSplitContext = ({
  rows,
  witnessId,
  rowId,
  segmentationResult,
  sourceText
}: {
  rows: AnchoredRow[]
  witnessId: string
  rowId: string
  segmentationResult: CompleteMicroSegmentationResult | null | undefined
  sourceText: string
}): LedgerSafeUnitSplitContext | null => {
  if (!witnessId || !rowId || !segmentationResult || !sourceText) {
    return null
  }

  const row = rows.find(candidate => candidate.id === rowId)
  if (!row) {
    return null
  }

  const mapping = getLedgerSafeActiveMapping(row, witnessId)
  if (!mapping?.unitUuid) {
    return null
  }

  const sourceContext = buildLedgerSafeWitnessSourceContext({ segmentationResult, sourceText })
  const resolved = resolveLedgerSafeMappingToOrderedIndex({
    mapping,
    orderedUnits: sourceContext.orderedUnits,
    orderedIndexByReference: sourceContext.orderedIndexByReference
  })

  return buildLedgerSafeUnitSplitContextForResolvedRow({
    witnessId,
    mappedRow: {
      row,
      rowId: row.id,
      originalUnitUuid: mapping.unitUuid,
      unitUuid: resolved?.unitUuid || mapping.unitUuid,
      unitLabel: mapping.unitLabel,
      orderedIndex: resolved?.orderedIndex ?? null
    },
    sourceContext
  })
}
